/* Offline-first layer: IndexedDB cache + write outbox + sync engine + conflicts. */

const DB_NAME = "hitech-offline";
const DB_VER = 1;
let _db = null;

function idb() {
  if (_db) return Promise.resolve(_db);
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VER);
    req.onupgradeneeded = () => {
      const d = req.result;
      if (!d.objectStoreNames.contains("cache")) d.createObjectStore("cache");
      if (!d.objectStoreNames.contains("outbox")) d.createObjectStore("outbox", { keyPath: "op_id" });
      if (!d.objectStoreNames.contains("conflicts")) d.createObjectStore("conflicts", { keyPath: "op_id" });
    };
    req.onsuccess = () => { _db = req.result; resolve(_db); };
    req.onerror = () => reject(req.error);
  });
}

function tx(store, mode, fn) {
  return idb().then((d) => new Promise((resolve, reject) => {
    const t = d.transaction(store, mode);
    const s = t.objectStore(store);
    const out = fn(s);
    t.oncomplete = () => resolve(out?.result !== undefined ? out.result : out);
    t.onerror = () => reject(t.error);
  }));
}

const getAll = (store) => idb().then((d) => new Promise((res, rej) => {
  const r = d.transaction(store).objectStore(store).getAll();
  r.onsuccess = () => res(r.result || []);
  r.onerror = () => rej(r.error);
}));

export const cacheSet = (key, data) => tx("cache", "readwrite", (s) => s.put({ data, at: Date.now() }, key)).catch(() => {});
export const cacheGet = (key) => idb().then((d) => new Promise((res) => {
  const r = d.transaction("cache").objectStore("cache").get(key);
  r.onsuccess = () => res(r.result ? r.result.data : undefined);
  r.onerror = () => res(undefined);
})).catch(() => undefined);

export const outboxAll = () => getAll("outbox").then((l) => l.sort((a, b) => a.queued_at - b.queued_at));
export const outboxAdd = (op) => tx("outbox", "readwrite", (s) => s.put(op));
export const outboxRemove = (id) => tx("outbox", "readwrite", (s) => s.delete(id));
export const conflictsAll = () => getAll("conflicts");
export const conflictAdd = (c) => tx("conflicts", "readwrite", (s) => s.put(c));
export const conflictRemove = (id) => tx("conflicts", "readwrite", (s) => s.delete(id));

/* ---- status events ---- */
export const purgeOffline = () => Promise.all(
  ["cache", "outbox", "conflicts"].map((s) => tx(s, "readwrite", (st) => st.clear()))
).then(() => emitSync({})).catch(() => {});

export const emitSync = (detail) => window.dispatchEvent(new CustomEvent("offline-sync", { detail }));
export const isOnline = () => navigator.onLine;

/* ---- offline-capable write endpoints ---- */
const OFFLINE_WRITE = [/^\/leads/, /^\/quotations/, /^\/invoices/, /^\/dispatch/, /^\/payments/];
export const isOfflineCapable = (url = "") => {
  const u = url.split("?")[0];
  if (u.includes("/pdf") || u.includes("customer-po") || u.includes("eway-file") || u.includes("razorpay")) return false;
  return OFFLINE_WRITE.some((re) => re.test(u));
};

const uid = () => `off-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

/* list cache key for a write url, e.g. /leads/xyz -> /leads */
const listKey = (url) => "/" + url.split("?")[0].split("/").filter(Boolean)[0];

/** Queue a write while offline; optimistically patch the cached list so UI keeps working. */
export async function queueWrite(cfg) {
  const method = cfg.method.toLowerCase();
  const url = cfg.url;
  let body = cfg.data;
  if (typeof body === "string") { try { body = JSON.parse(body); } catch { body = {}; } }
  if (body instanceof FormData) throw new Error("File uploads need internet");
  const lk = listKey(url);
  const list = (await cacheGet(lk)) || [];
  const opId = uid();
  let baseUpdatedAt = "";
  let tempRecord = null;
  const parts = url.split("?")[0].split("/").filter(Boolean);
  const recId = parts[1] || "";

  if (method === "post") {
    tempRecord = { id: `offline-${opId}`, number: "DRAFT", status: "Draft", _offline: true,
                   created_at: new Date().toISOString(), updated_at: new Date().toISOString(), ...body };
    await cacheSet(lk, [tempRecord, ...list]);
  } else if (method === "put" && recId) {
    const cur = list.find((r) => r.id === recId);
    baseUpdatedAt = cur?.updated_at || "";
    await cacheSet(lk, list.map((r) => (r.id === recId ? { ...r, ...body, _offline: true } : r)));
  } else if (method === "delete" && recId) {
    await cacheSet(lk, list.filter((r) => r.id !== recId));
  }
  await outboxAdd({ op_id: opId, method, url, body, base_updated_at: baseUpdatedAt,
                    queued_at: Date.now(), label: `${method.toUpperCase()} ${url}` });
  emitSync({ type: "queued" });
  return tempRecord || { ok: true, _offline: true };
}

/* ---- sync engine ---- */
let syncing = false;

export async function runSync(apiInstance) {
  if (syncing || !navigator.onLine) return;
  const ops = await outboxAll();
  if (!ops.length) { emitSync({ type: "idle" }); return; }
  syncing = true;
  emitSync({ type: "syncing", count: ops.length });
  const idMap = {}; // offline temp id -> real id
  let synced = 0, conflicted = 0, failed = 0;
  for (const op of ops) {
    let url = op.url;
    Object.entries(idMap).forEach(([t, real]) => { url = url.replace(t, real); });
    try {
      const headers = { "X-Offline-Sync": "1" };
      if (op.method === "put" && op.base_updated_at) headers["X-Base-Updated-At"] = op.base_updated_at;
      const r = await apiInstance.request({ method: op.method, url, data: op.body, headers, _skipOffline: true });
      if (op.method === "post" && r.data?.id) {
        const temp = `offline-${op.op_id}`;
        idMap[temp] = r.data.id;
      }
      await outboxRemove(op.op_id);
      synced += 1;
    } catch (e) {
      if (e?.response?.status === 409) {
        const server = e.response.data?.detail?.server || null;
        await conflictAdd({ ...op, url, server, detected_at: Date.now() });
        await outboxRemove(op.op_id);
        conflicted += 1;
      } else if (!e.response) {
        failed += 1; break; // went offline again — stop, keep remaining ops
      } else {
        await conflictAdd({ ...op, url, server: null, error: e.response?.data?.detail || e.message, detected_at: Date.now() });
        await outboxRemove(op.op_id);
        conflicted += 1;
      }
    }
  }
  syncing = false;
  emitSync({ type: "done", synced, conflicted, failed });
  return { synced, conflicted, failed };
}
